import React, { useCallback } from 'react';
import { X, Play, Heart, Star, Clock, Calendar, User, Film, Plus, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFavoritesContext } from '@/context/FavoritesContext';
import { getCustomVideoUrl } from '@/data/customVideos';

const MovieModal = ({ movie, isOpen, onClose }) => {
  const { isFavorite, toggleFavorite } = useFavoritesContext();
  const navigate = useNavigate();

  const isMovieFavorite = movie ? isFavorite(movie.id) : false;

  // Check for custom video (optional usage here)
  const customVideoUrl = movie ? getCustomVideoUrl(movie.title, movie.year || movie.release_date?.split('-')[0]) : null;

  const handleToggleFavorite = useCallback(() => {
    if (movie) {
      toggleFavorite(movie.id);
    }
  }, [movie, toggleFavorite]);

  const handlePlayClick = () => {
    onClose(); // Close modal
    navigate(`/movie/${movie.id}`);
  };

  if (!movie) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-card border-border overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Backdrop Image */}
        <div className="relative w-full aspect-video">
          <img
            src={movie.backdrop}
            alt={movie.title}
            className="w-full h-full object-cover"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/60 hover:bg-background/90 backdrop-blur-sm text-foreground z-10"
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Button
              size="lg"
              onClick={handlePlayClick}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-foreground/90 hover:bg-foreground text-background shadow-2xl transition-transform duration-300 hover:scale-110"
            >
              <Play className="w-8 h-8 md:w-10 md:h-10 fill-current ml-1" />
            </Button>
          </div>

          {/* Title and Basic Info on Image */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2 text-shadow-hero">
              {movie.title}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {movie.originalTitle}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={handlePlayClick}
              className="flex-1 min-w-[150px] h-12 bg-foreground text-background hover:bg-foreground/90 font-bold gap-2"
            >
              <Play className="w-5 h-5 fill-current" />
              <span>Reproducir</span>
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={handleToggleFavorite}
              className={cn(
                'h-12 px-6 gap-2 border-border',
                isMovieFavorite
                  ? 'bg-primary/20 border-primary text-primary hover:bg-primary/30'
                  : 'hover:bg-secondary'
              )}
            >
              {isMovieFavorite ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>En Mi Lista</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Mi Lista</span>
                </>
              )}
            </Button>
          </div>

          {/* Movie Stats */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 py-4 border-y border-border">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 fill-accent text-accent" />
              <span className="text-foreground font-bold">{movie.rating}</span>
              <span className="text-muted-foreground text-sm">/10</span>
            </div>

            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{movie.year}</span>
            </div>

            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{movie.duration}</span>
            </div>

            <span className="px-2 py-1 bg-secondary text-muted-foreground text-xs font-medium rounded">
              {movie.maturity}
            </span>
          </div>

          {/* Genres */}
          <div className="flex flex-wrap gap-2">
            {movie.genres.map((genre, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-secondary text-foreground text-sm font-medium rounded-full"
              >
                {genre}
              </span>
            ))}
          </div>

          {/* Tagline */}
          <p className="text-lg md:text-xl text-primary font-medium italic">
            "{movie.tagline}"
          </p>

          {/* Description */}
          <div>
            <h3 className="text-foreground font-semibold mb-2">Sinopsis</h3>
            <p className="text-muted-foreground leading-relaxed">
              {movie.description}
            </p>
          </div>

          {/* Credits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
            {/* Director */}
            <div>
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Film className="w-4 h-4" />
                <span className="text-sm font-medium">Director</span>
              </div>
              <p className="text-foreground">{movie.director}</p>
            </div>

            {/* Cast */}
            <div>
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Reparto Principal</span>
              </div>
              <p className="text-foreground">{movie.cast.join(', ')}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MovieModal;
